import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { getApiErrorMessage, MUTATION_OFFLINE_MESSAGE } from '@/lib/network/apiErrors';
import { useNetwork } from '@/lib/network/NetworkProvider';
import MobileShell from '@/components/MobileShell';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { openRazorpayCheckout } from '@/lib/razorpay';
import { X, QrCode, CreditCard, ShieldCheck } from 'lucide-react';

const QR_ASSET = '/assets/resqly-phonepe-qr.svg';

export default function ConsumerBookingPayment() {
  const { bookingId } = useParams();
  const { isOnline } = useNetwork();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [paymentAttempted, setPaymentAttempted] = useState(false);
  const [showChoice, setShowChoice] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}`);
      setBooking(data.booking);
    } catch {
      toast.error('Could not load booking');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    load();
  }, [load]);

  const startRazorpay = async () => {
    if (!booking || booking.status !== 'payment_pending') return;
    if (!isOnline) {
      toast.error(MUTATION_OFFLINE_MESSAGE);
      return;
    }

    setPaying(true);
    setShowChoice(false);

    try {
      const { data } = await api.post(`/bookings/${bookingId}/payment/order`, {});
      const checkout = data.checkout;

      await openRazorpayCheckout({
        checkout,
        description: 'Service booking payment',
        onSuccess: async (response) => {
          try {
            await api.post(`/bookings/${bookingId}/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success('Payment successful');
            await load();
            window.location.href = `/consumer/bookings/${bookingId}/tracking`;
          } catch (e) {
            toast.error(getApiErrorMessage(e, 'Payment verification failed'));
            setPaymentAttempted(true);
            await load();
          } finally {
            setPaying(false);
          }
        },
        onDismiss: () => {
          setPaying(false);
          setPaymentAttempted(true);
        },
        onFailed: () => {
          toast.error('Payment failed');
          setPaying(false);
          setPaymentAttempted(true);
          load();
        },
      });
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not start payment'));
      setPaying(false);
      setPaymentAttempted(true);
    }
  };

  const pay = () => {
    if (paymentAttempted) {
      setShowChoice(true);
      return;
    }
    startRazorpay();
  };

  const openQrFallback = () => {
    setShowChoice(false);
    setShowQr(true);
  };

  if (loading) {
    return (
      <MobileShell title="Booking Payment">
        <div className="p-5 text-sm text-slate-500">Loading…</div>
      </MobileShell>
    );
  }

  if (!booking) {
    return (
      <MobileShell title="Booking Payment">
        <div className="p-5 text-sm text-slate-500">Booking not found.</div>
      </MobileShell>
    );
  }

  const quote = booking.quote;
  const isPaid = booking.status === 'confirmed' || booking.payment?.status === 'paid';
  const total = Number(quote?.customer_total || 0).toFixed(2);

  return (
    <MobileShell title="Booking Payment">
      <div className="resqly-content px-4 sm:px-5 py-5 space-y-4" data-testid="booking-payment-page">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-semibold text-slate-700">Status</span>
          <span
            data-testid="booking-payment-status"
            className={`text-xs font-semibold px-2 py-1 rounded-full shrink-0 ${
              isPaid ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
            }`}
          >
            {isPaid ? 'Payment complete' : 'Payment pending'}
          </span>
        </div>

        {quote?.line_items?.length > 0 && (
          <div className="resqly-card p-4 space-y-2">
            {quote.line_items.map((item) => (
              <div key={item.key} className="flex justify-between gap-4 text-sm">
                <span className="text-slate-600 min-w-0 break-words">{item.label}</span>
                <span className="font-medium text-slate-900 shrink-0">
                  ₹{Number(item.amount).toFixed(2)}
                </span>
              </div>
            ))}
            <div className="border-t pt-2 flex justify-between gap-4 text-sm font-semibold">
              <span>Total</span>
              <span data-testid="booking-payment-total" className="shrink-0">₹{total}</span>
            </div>
          </div>
        )}

        {!isPaid && booking.status === 'payment_pending' && (
          <div className="space-y-2">
            <Button
              data-testid="booking-pay-button"
              className="w-full min-h-12 bg-slate-900 hover:bg-slate-800"
              disabled={paying || !isOnline}
              onClick={pay}
            >
              {paying ? 'Opening checkout…' : `Pay ₹${total}`}
            </Button>
            {paymentAttempted && (
              <p className="text-center text-xs text-slate-500 px-3">
                If Razorpay did not work, tap Pay again to choose another payment option.
              </p>
            )}
          </div>
        )}

        <Link to="/consumer/home" className="block text-center text-sm text-blue-700 font-semibold py-2">
          Back to Home
        </Link>
      </div>

      {showChoice && (
        <div className="fixed inset-0 z-[80] bg-slate-950/50 flex items-end sm:items-center justify-center p-3 sm:p-5">
          <div className="w-full max-w-[480px] bg-white rounded-3xl shadow-2xl overflow-hidden resqly-modal-scroll">
            <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Choose how to pay</h2>
                <p className="text-sm text-slate-500 mt-1">Your previous Razorpay attempt did not complete.</p>
              </div>
              <button type="button" onClick={() => setShowChoice(false)} className="p-2 rounded-full hover:bg-slate-100" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <button type="button" onClick={startRazorpay} className="w-full text-left p-4 rounded-2xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/40 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center"><CreditCard className="w-5 h-5" /></span>
                  <span className="min-w-0"><span className="block font-semibold text-slate-900">Continue with Razorpay</span><span className="block text-xs text-slate-500 mt-0.5">Retry cards, UPI, net banking and wallets.</span></span>
                </div>
              </button>
              <button type="button" onClick={openQrFallback} className="w-full text-left p-4 rounded-2xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center"><QrCode className="w-5 h-5" /></span>
                  <span className="min-w-0"><span className="block font-semibold text-slate-900">Pay directly to RESQLY</span><span className="block text-xs text-slate-500 mt-0.5">Scan the official RESQLY PhonePe QR code.</span></span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {showQr && (
        <div className="fixed inset-0 z-[90] bg-slate-950/70 flex items-center justify-center p-3 sm:p-5">
          <div className="w-full max-w-[460px] max-h-[94dvh] overflow-y-auto bg-white rounded-3xl shadow-2xl">
            <div className="p-5 flex items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 text-emerald-700 text-xs font-bold uppercase tracking-wider"><ShieldCheck className="w-4 h-4" /> RESQLY direct payment</div>
                <h2 className="text-xl font-bold text-slate-900 mt-1">Scan & pay ₹{total}</h2>
                <p className="text-sm text-slate-500 mt-1">Use PhonePe or another UPI app to scan this QR.</p>
              </div>
              <button type="button" onClick={() => setShowQr(false)} className="p-2 rounded-full hover:bg-slate-100" aria-label="Close QR payment"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-5 pb-5">
              <div className="rounded-2xl bg-slate-950 p-4 flex justify-center">
                <img src={QR_ASSET} alt="RESQLY PhonePe payment QR code" className="w-full max-w-[320px] aspect-square object-contain bg-white rounded-xl" />
              </div>
              <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-100 p-4 text-xs leading-relaxed text-amber-900">
                <strong>Important:</strong> Please pay exactly ₹{total}. Direct QR payments are not automatically verified by Razorpay. Keep your payment confirmation/UTR ready so RESQLY can verify the payment before the booking is confirmed.
              </div>
              <Button type="button" variant="outline" className="w-full mt-4 min-h-12" onClick={() => { setShowQr(false); toast.success('Payment details noted. Please keep your UPI confirmation/UTR for verification.'); }}>
                I’ve completed the payment
              </Button>
            </div>
          </div>
        </div>
      )}
    </MobileShell>
  );
}
